import { z } from "zod";
import { idbResult, localTransaction, notifyLocalDatabaseChanged } from "./operator-local-database";
import { claimQuantityAttempt, decodeQuantityQueue, quantityWriteSchema, recoverInterruptedWrite,
  type PendingQuantityWrite, type QuantityWriteIdentity } from "./quantity-write-queue";

export type LocalDraftRef = { key: string; intentId: string; revision: number };
const draftRefSchema = z.object({ key: z.string(), intentId: z.string().uuid(), revision: z.number().int().nonnegative() });
export async function listQuantityOutbox(preview: boolean, identity: Pick<QuantityWriteIdentity, "terminalCode" | "operatorId">) {
  const values: unknown = await localTransaction(preview, ["writes"], "readonly", (tx) =>
    idbResult(tx.objectStore("writes").index("owner").getAll([identity.terminalCode, identity.operatorId])));
  return z.array(quantityWriteSchema).parse(values).map((item) => recoverInterruptedWrite(item, Date.now()))
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));
}
export async function enqueueQuantityOutbox(preview: boolean, item: PendingQuantityWrite, draft?: LocalDraftRef) {
  const validated = quantityWriteSchema.parse(item);
  await localTransaction(preview, ["writes", "drafts"], "readwrite", async (tx) => {
    const writes = tx.objectStore("writes");
    const existing: unknown = await idbResult(writes.get(validated.idempotencyKey));
    if (existing !== undefined) throw new Error("Bu taslak zaten gönderim kuyruğunda. Bekleyen kayıtları kontrol edin.");
    const count = await idbResult(writes.index("owner").count([item.terminalCode, item.operatorId]));
    if (count >= 64) throw new Error("Bu cihazdaki 64 bekleyen kaydı kontrol etmeden yeni kayıt eklenemez.");
    if (draft) {
      const stored: unknown = await idbResult(tx.objectStore("drafts").get(draft.key));
      const current = draftRefSchema.safeParse(stored);
      if (!current.success || current.data.intentId !== draft.intentId || current.data.revision !== draft.revision) {
        throw new Error("Taslak başka bir pencerede değişti veya zaten kuyruğa alındı. Güncel taslağı kontrol edin.");
      }
    }
    await idbResult(writes.add(validated));
    if (draft) await idbResult(tx.objectStore("drafts").delete(draft.key));
  });
  notifyLocalDatabaseChanged();
}
export async function claimQuantityOutbox(preview: boolean, id: string, identity: QuantityWriteIdentity) {
  return localTransaction(preview, ["writes"], "readwrite", async (tx) => {
    const store = tx.objectStore("writes"), raw: unknown = await idbResult(store.get(id));
    if (raw === undefined) return null;
    const next = claimQuantityAttempt(quantityWriteSchema.parse(raw), identity, Date.now());
    if (next) await idbResult(store.put(next));
    return next;
  });
}
/** Compare the attempt number so a late timeout never overwrites a newer result or resurrects a removed record. */
export async function finishQuantityAttempt(preview: boolean, item: PendingQuantityWrite, next: PendingQuantityWrite | null) {
  await localTransaction(preview, ["writes"], "readwrite", async (tx) => {
    const store = tx.objectStore("writes"), raw: unknown = await idbResult(store.get(item.idempotencyKey));
    if (raw === undefined) return;
    const current = quantityWriteSchema.parse(raw);
    if (next && current.attempts !== item.attempts) return;
    if (next) await idbResult(store.put(next)); else await idbResult(store.delete(item.idempotencyKey));
  });
  notifyLocalDatabaseChanged();
}
export async function dismissRejectedQuantity(preview: boolean, id: string, identity: QuantityWriteIdentity) {
  await localTransaction(preview, ["writes"], "readwrite", async (tx) => {
    const store = tx.objectStore("writes"), raw: unknown = await idbResult(store.get(id));
    if (raw === undefined) return;
    const item = quantityWriteSchema.parse(raw);
    if (item.operatorId !== identity.operatorId || item.terminalCode !== identity.terminalCode ||
      item.status !== "FAILED" || item.everUncertain) throw new Error("Sonucu belirsiz kayıt silinemez; önce sunucudan doğrulayın.");
    await idbResult(store.delete(id));
  });
  notifyLocalDatabaseChanged();
}
export async function importLegacyQuantityOutbox(preview: boolean, identity: QuantityWriteIdentity) {
  const key = `anl-quantity-writes:v1:${preview ? "demo" : "live"}:${identity.terminalCode}:${identity.operatorId}`;
  const text = window.localStorage.getItem(key);
  if (!text) return;
  const items = decodeQuantityQueue(text);
  await localTransaction(preview, ["writes"], "readwrite", async (tx) => {
    const store = tx.objectStore("writes");
    for (const item of items) {
      const existing: unknown = await idbResult(store.get(item.idempotencyKey));
      if (existing === undefined) await idbResult(store.add({ ...item, everUncertain: item.status !== "UNSENT", attempts: item.status === "UNSENT" ? 0 : 1 }));
    }
  });
  // Only remove the exact imported value; another legacy tab may have updated it meanwhile.
  if (window.localStorage.getItem(key) === text) window.localStorage.removeItem(key);
}
